// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import type Svg from 'react-native-svg';

import GoffyButton from '@/components/GoffyButton';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { buildResultsLink, resolveTourInviteLink } from '@/services/tourInviteLink';
import { useInviteViewer } from '@/hooks/useInviteViewer';
import { ShareLeaderboardCard } from '@/components/ShareLeaderboardCard';
import { CARD_WIDTH, layoutShareCard, type ShareCardContent } from '@/components/shareCardLayout';
import { sharePngBase64 } from '@/services/shareImage';
import appColors from '@/theme/colors';

type ShareLinkButtonProps = {
  // Tour the shareable invite link is generated for. Without one the modal drops the join
  // code and QR (e.g. anonymous invite view or a tour-less round) and offers only the image.
  tourId?: string | null;
  // In-app route of the results this page shows (`/tour/x/leaderboard`, `/round/y/summary?tourId=x`).
  // Supplying it adds "Share results link": the same page, opened read-only with no menus, for
  // anyone holding the link. See buildResultsLink.
  resultsPath?: string;
  // Results to draw into a shareable picture. Supplying it adds "Share image" to the modal.
  image?: ShareCardContent;
  // Base name for the exported file, without extension.
  imageFileName?: string;
  // Share the results link straight from the button: no modal, no join code, no QR, no image.
  // The tourId is still needed — it is what resolves the invite token the results link is built
  // from — it just stops being presented. For a page whose point is the numbers.
  resultsOnly?: boolean;
  // Tone of the button that opens the modal. Accent (green) reads well on the app's dark
  // screens; a page on white paper wants the dark pill instead.
  tone?: 'dark' | 'accent';
  // Box of the button that opens the modal, for a page that needs it smaller than the app's
  // standard compact size. Text styling is GoffyButton's own and stays put.
  triggerStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
};

const CAPTURE_TIMEOUT_MS = 10_000;

const slugify = (value: string) =>
  value
    // Strip the diacritics so "Höstserien" exports as hostserien-... rather than h-stserien-...
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'goffy';

/**
 * Top-of-page "Share" button. Opens the same invite-share modal used on the
 * tournaments and round-start screens: it resolves the tour invite link via the
 * BFF and presents the join code, a QR code, and a Share action (native share
 * sheet, or the Web Share API / clipboard fallback on web).
 *
 * On the round and tour leaderboards it also carries "Share image", which exports the results
 * themselves as a PNG for a chat thread — the same modal, so there is one share entry point
 * per page rather than two buttons competing for the same corner.
 *
 * With `resultsOnly` there is no modal: the button hands the results link straight to the share
 * sheet. A page offering exactly one action should not ask for a click to confirm it.
 */
export function ShareLinkButton({
  tourId,
  resultsPath,
  image,
  imageFileName,
  resultsOnly,
  tone = 'accent',
  triggerStyle,
  style,
}: ShareLinkButtonProps) {
  const { t } = useI18n();
  const { user } = useUser();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageMessage, setImageMessage] = useState<string | null>(null);
  const cardRef = useRef<Svg>(null);
  const prefetchStarted = useRef(false);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);
  const moreLabel = (count: number) => t('shareImage.andMore', { count });
  const [resultsMessage, setResultsMessage] = useState<string | null>(null);
  // A spectator has no tour in context (tourId is null for them) but does hold an invite token in
  // the URL — enough to mint a results link of their own.
  const { inviteToken: viewerInviteToken } = useInviteViewer();

  const joinCode = useMemo(() => {
    if (!link) {
      return null;
    }
    const token = decodeURIComponent(link.split('/').pop() ?? '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim()
      .toUpperCase();
    if (!token) {
      return null;
    }
    return token.length > 6 ? token.slice(0, 6) : token;
  }, [link]);

  // A card with nothing in it is not worth offering; the pages hand over empty standings while
  // their leaderboard request is still in flight.
  const hasImage = Boolean(image && image.standings.length > 0);
  const inviteTokenFromLink = link ? decodeURIComponent(link.split('/').pop() ?? '').trim() : '';
  const resultsToken = inviteTokenFromLink || viewerInviteToken;
  const hasResults = Boolean(resultsPath) && (Boolean(tourId) || Boolean(viewerInviteToken));
  const resultsLink = resultsPath && resultsToken ? buildResultsLink(resultsPath, resultsToken) : null;

  /**
   * With no modal to resolve the invite token in, the token has to be in hand BEFORE the press:
   * the Web Share API and the clipboard both require the call to happen inside the gesture, and
   * awaiting a fetch first loses that and throws NotAllowedError. So fetch it on mount instead.
   */
  useEffect(() => {
    // Guarded by a ref, not by state: writing state here would re-run the effect, and an effect
    // that cancels its own in-flight fetch on every re-run never resolves anything.
    if (!resultsOnly || !tourId || prefetchStarted.current) {
      return;
    }
    prefetchStarted.current = true;
    resolveTourInviteLink({ apiBaseUrl, tourId, authToken: user?.token })
      .then((inviteLink) => {
        if (mounted.current) {
          setLink(inviteLink);
        }
      })
      .catch(() => {
        if (mounted.current) {
          setError(t('tournaments.shareLoadError'));
        }
      });
  }, [apiBaseUrl, resultsOnly, t, tourId, user?.token]);
  const previewWidth = Math.min(320, Math.max(220, Dimensions.get('window').width - 96));

  // After every hook: an early return above a hook makes the hook count differ
  // between renders once tourId resolves (null on mount, set by an effect on the
  // tour leaderboard), which crashes React with "Rendered more hooks than during
  // the previous render" and trips the app error boundary.
  if (!tourId && !hasImage && !hasResults) {
    return null;
  }

  const handleOpen = async () => {
    setOpen(true);
    setError(null);
    setImageMessage(null);
    setResultsMessage(null);
    if (!tourId) {
      return;
    }
    setLoading(true);
    setLink(null);
    try {
      const inviteLink = await resolveTourInviteLink({ apiBaseUrl, tourId, authToken: user?.token });
      setLink(inviteLink);
    } catch {
      setError(t('tournaments.shareLoadError'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * react-native-svg's `toDataURL` renders the card to a PNG on native and through a canvas on
   * web, so the export needs no screenshot dependency. It reports failure by simply never calling
   * back, hence the timeout — without it the Share button would spin forever.
   */
  const captureCard = () =>
    new Promise<string>((resolve, reject) => {
      const node = cardRef.current as unknown as {
        toDataURL?: (
          callback: (data: string) => void,
          options?: { width?: number; height?: number }
        ) => void;
      } | null;
      if (!image || typeof node?.toDataURL !== 'function') {
        reject(new Error('card capture unavailable'));
        return;
      }
      const timer = setTimeout(() => reject(new Error('card capture timed out')), CAPTURE_TIMEOUT_MS);
      try {
        node.toDataURL(
          (data) => {
            clearTimeout(timer);
            if (data) {
              resolve(data);
            } else {
              reject(new Error('card capture returned no data'));
            }
          },
          { width: CARD_WIDTH, height: layoutShareCard(image).height }
        );
      } catch (err) {
        clearTimeout(timer);
        reject(err);
      }
    });

  const handleShareImage = async () => {
    if (!hasImage) {
      return;
    }
    setImageBusy(true);
    setError(null);
    setImageMessage(null);
    try {
      const base64 = await captureCard();
      const outcome = await sharePngBase64(
        base64,
        `${slugify(imageFileName ?? 'goffy-results')}.png`,
        t('shareImage.action')
      );
      if (outcome === 'downloaded') {
        setImageMessage(t('shareImage.downloaded'));
      } else if (outcome === 'unsupported') {
        setError(t('shareImage.unsupported'));
      } else if (outcome === 'shared') {
        setOpen(false);
      }
    } catch {
      setError(t('shareImage.error'));
    } finally {
      setImageBusy(false);
    }
  };

  /**
   * Native share sheet, or the Web Share API with a clipboard fallback on web. Returns 'copied'
   * when it fell back, so the caller can say so — a silent copy reads as a dead button.
   */
  const shareUrl = async (url: string, title: string): Promise<'shared' | 'copied' | 'cancelled'> => {
    if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
      const nav = navigator as {
        share?: (data: { title?: string; url?: string }) => Promise<void>;
        clipboard?: { writeText?: (value: string) => Promise<void> };
      };
      if (typeof nav.share === 'function') {
        await nav.share({ title, url });
        return 'shared';
      }
      if (nav.clipboard?.writeText) {
        await nav.clipboard.writeText(url);
        return 'copied';
      }
      throw new Error('share unsupported');
    }
    const result = await Share.share({ title, message: url });
    return result.action === Share.dismissedAction ? 'cancelled' : 'shared';
  };

  const isCancellation = (err: unknown) =>
    err instanceof Error && err.message.toLowerCase().includes('cancel');

  const handleShare = async () => {
    if (!link) {
      return;
    }
    setError(null);
    setResultsMessage(null);
    try {
      const outcome = await shareUrl(link, t('tournaments.shareAction'));
      if (outcome === 'copied') {
        setResultsMessage(t('tournaments.shareCopied'));
      }
    } catch (err) {
      if (isCancellation(err)) {
        return; // user dismissed the share sheet
      }
      setError(t('tournaments.shareSendError'));
    }
  };

  const handleShareResults = async () => {
    if (!resultsLink) {
      return;
    }
    setError(null);
    setResultsMessage(null);
    try {
      const outcome = await shareUrl(resultsLink, t('shareResults.action'));
      if (outcome === 'copied') {
        setResultsMessage(t('shareResults.copied'));
      }
    } catch (err) {
      if (isCancellation(err)) {
        return;
      }
      setError(t('shareResults.error'));
    }
  };

  if (resultsOnly) {
    return (
      <View style={[styles.directColumn, style]}>
        <GoffyButton
          label={t('tournaments.shareAction')}
          onPress={() => void handleShareResults()}
          disabled={!resultsLink}
          tone={tone}
          size="compact"
          oldButtonStyle={triggerStyle}
          accessibilityLabel={t('tournaments.shareAction')}
          testID="share-results-action"
        />
        {/* The modal used to carry these; without one they belong under the button, or a
            clipboard fallback would look like a dead press. */}
        {resultsMessage ? <Text style={styles.directNote}>{resultsMessage}</Text> : null}
        {error ? <Text style={[styles.error, styles.directNote]}>{error}</Text> : null}
      </View>
    );
  }

  return (
    <View style={[styles.row, style]}>
      <GoffyButton
        label={t('tournaments.shareAction')}
        onPress={() => void handleOpen()}
        tone={tone}
        size="compact"
        oldButtonStyle={triggerStyle}
        accessibilityLabel={t('tournaments.shareAction')}
      />

      <Modal transparent visible={open} animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.shareOverlay} onPress={() => setOpen(false)}>
          <Pressable style={styles.shareCard} onPress={() => null}>
            <Text style={styles.shareTitle}>
              {tourId ? t('players.inviteOthers') : t('tournaments.shareCta')}
            </Text>
            {tourId ? (
              <View style={styles.shareCodeCard}>
                <Text style={styles.shareCodeLabel}>{t('players.joinWithCode')}</Text>
                <Text style={styles.shareCodeValue}>{joinCode || '--- ---'}</Text>
                {loading ? <ActivityIndicator size="small" color={appColors.accent.primary} /> : null}
                {link ? (
                  <View style={styles.shareQrWrapper}>
                    <QRCode value={link} size={220} color="#000000" backgroundColor="#ffffff" />
                  </View>
                ) : null}
              </View>
            ) : null}
            {image && hasImage ? (
              <ScrollView contentContainerStyle={styles.sharePreview}>
                <ShareLeaderboardCard ref={cardRef} {...image} moreLabel={moreLabel} previewWidth={previewWidth} />
              </ScrollView>
            ) : null}
            {imageBusy ? (
              <View style={styles.shareImageStatus}>
                <ActivityIndicator size="small" color={appColors.accent.primary} />
                <Text style={styles.shareImageStatusText}>{t('shareImage.preparing')}</Text>
              </View>
            ) : null}
            {imageMessage ? <Text style={styles.shareImageStatusText}>{imageMessage}</Text> : null}
            {resultsMessage ? <Text style={styles.shareImageStatusText}>{resultsMessage}</Text> : null}
            {hasResults ? <Text style={styles.shareHint}>{t('shareResults.hint')}</Text> : null}
            {error ? <Text style={styles.error}>{error}</Text> : null}
            <View style={styles.shareActions}>
              {tourId ? (
                <GoffyButton
                  label={t('tournaments.shareAction')}
                  onPress={() => void handleShare()}
                  disabled={!link || loading}
                  tone="accent"
                  size="compact"
                  style={styles.shareActionButton}
                />
              ) : null}
              {hasResults ? (
                <GoffyButton
                  label={t('shareResults.action')}
                  onPress={() => void handleShareResults()}
                  disabled={!resultsLink || loading}
                  tone={tourId ? 'dark' : 'accent'}
                  size="compact"
                  style={styles.shareActionButton}
                  testID="share-results-action"
                />
              ) : null}
              {hasImage ? (
                <GoffyButton
                  label={t('shareImage.action')}
                  onPress={() => void handleShareImage()}
                  disabled={imageBusy}
                  tone="dark"
                  size="compact"
                  style={styles.shareActionButton}
                  testID="share-image-action"
                />
              ) : null}
              <GoffyButton
                label={t('tournaments.shareClose')}
                onPress={() => setOpen(false)}
                tone="dark"
                size="compact"
                style={styles.shareActionButton}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    width: '100%',
  },
  directColumn: {
    alignItems: 'flex-end',
    gap: 4,
  },
  directNote: {
    fontSize: 12,
    textAlign: 'right',
    maxWidth: 200,
  },
  shareOverlay: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  shareCard: {
    width: '100%',
    maxWidth: 420,
    maxHeight: '90%',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#07200D',
    padding: 18,
    gap: 12,
  },
  shareTitle: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  shareCodeCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#04210D',
    padding: 16,
    gap: 10,
    alignItems: 'center',
  },
  shareCodeLabel: {
    color: '#8FA08B',
    fontSize: 12,
    letterSpacing: 3,
    textTransform: 'uppercase',
    fontWeight: '700',
  },
  shareCodeValue: {
    color: '#AEE79A',
    fontSize: 48,
    letterSpacing: 6,
    fontWeight: '700',
    textAlign: 'center',
  },
  shareQrWrapper: {
    alignSelf: 'center',
    borderRadius: 12,
    backgroundColor: '#f6f8f6',
    padding: 10,
  },
  sharePreview: {
    alignItems: 'center',
    paddingVertical: 4,
  },
  shareHint: {
    color: appColors.text.muted,
    fontSize: 13,
    textAlign: 'center',
  },
  shareImageStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  shareImageStatusText: {
    color: appColors.text.secondary,
    textAlign: 'center',
  },
  shareActions: {
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'flex-start',
  },
  shareActionButton: {
    width: '100%',
  },
  error: {
    color: '#ff7b7b',
    marginTop: 4,
  },
});
